import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { limsClient } from '../api/limsClient';
import type { SourceFileResponse } from '@mariana/labos-client';
import './XmlFileViewer.css';

interface XmlFileViewerProps {
  /** Full s3:// path of the XML file to view. */
  path: string;
  onClose: () => void;
}

const MIN_WIDTH = 360;
const DEFAULT_WIDTH = 640;
// Server caps the returned content at 15 MB (MAX_SOURCE_FILE_BYTES); keep in sync.
const MAX_DISPLAY_MB = 15;

/** A single element node in the friendly tree view. Renders children lazily. */
const XmlNode: React.FC<{ el: Element; depth: number }> = ({ el, depth }) => {
  const childEls = Array.from(el.children);
  const hasChildren = childEls.length > 0;
  const [open, setOpen] = useState(depth < 2);
  const attrs = Array.from(el.attributes);
  const textValue = hasChildren ? '' : (el.textContent || '').trim();

  return (
    <div className="xt-node">
      <div className="xt-row" style={{ paddingLeft: depth * 14 }}>
        {hasChildren ? (
          <button className="xt-toggle" onClick={() => setOpen((o) => !o)} aria-label={open ? 'Collapse' : 'Expand'}>
            {open ? '\u25be' : '\u25b8'}
          </button>
        ) : (
          <span className="xt-toggle-spacer" />
        )}
        <span className="xt-name">{el.tagName}</span>
        {attrs.map((a) => (
          <span key={a.name} className="xt-attr">
            <span className="xt-attr-k">{a.name}</span>
            <span className="xt-attr-eq">=</span>
            <span className="xt-attr-v">{a.value}</span>
          </span>
        ))}
        {!hasChildren && textValue && (
          <>
            <span className="xt-colon">:</span>
            <span className="xt-value">{textValue}</span>
          </>
        )}
      </div>
      {hasChildren && open && (
        <div className="xt-children">
          {childEls.map((c, i) => (
            <XmlNode key={i} el={c} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

export const XmlFileViewer: React.FC<XmlFileViewerProps> = ({ path, onClose }) => {
  const [data, setData] = useState<SourceFileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const draggingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    limsClient
      .getSourceFile(path)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load file');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    const onMove = (ev: MouseEvent) => {
      if (!draggingRef.current) return;
      const next = window.innerWidth - ev.clientX;
      setWidth(Math.max(MIN_WIDTH, Math.min(next, window.innerWidth - 120)));
    };
    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Parsed document for the tree view; null (and a plain-text fallback) on parse error.
  const docRoot = useMemo(() => {
    if (!data) return null;
    try {
      const doc = new DOMParser().parseFromString(data.content, 'application/xml');
      if (doc.getElementsByTagName('parsererror').length > 0) return null;
      return doc.documentElement ?? null;
    } catch {
      return null;
    }
  }, [data]);

  return (
    <div className="xml-viewer-panel" style={{ width }} role="dialog" aria-label={`XML viewer: ${path}`}>
      <div className="xml-viewer-resizer" onMouseDown={startResize} />
      <div className="xml-viewer-header">
        <span className="xml-viewer-title" title={path}>
          {data?.fileName ?? path.split('/').pop() ?? path}
        </span>
        <div className="xml-viewer-actions">
          <button className="xml-viewer-btn xml-viewer-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
      </div>

      {loading && (
        <div className="xml-viewer-status">
          <div className="loading-spinner" />
          <div className="loading-text">Loading XML…</div>
        </div>
      )}

      {error && !loading && (
        <div className="xml-viewer-status xml-viewer-error">
          <p>Failed to load file.</p>
          <p className="xml-viewer-error-detail">{error}</p>
        </div>
      )}

      {data && !loading && !error && (
        docRoot ? (
          <div className="xml-viewer-tree">
            <XmlNode el={docRoot} depth={0} />
          </div>
        ) : (
          <pre className="xml-viewer-fallback">{data.content}</pre>
        )
      )}

      {data?.truncated && !loading && !error && (
        <div className="xml-viewer-truncated">
          File is {(data.size / (1024 * 1024)).toFixed(1)} MB and only the first {MAX_DISPLAY_MB} MB is shown.
          For the complete file, fetch it from S3 at{' '}
          <code className="xml-viewer-truncated-path">{path}</code>.
        </div>
      )}
    </div>
  );
};
